import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as GoodsApi from '../api/Goods';
import * as GoodsTagsApi from '../api/GoodsTags';
import * as MpProductsApi from '../api/MpProducts';
import type { MpAuthOption } from '../api/MpProducts';
import {
  GoodsProps,
  GoodsQueryParams,
  GoodsPaginatedResponse,
} from '../api/Goods';

type TriBool = '' | 'true' | 'false';

export const useGoods = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const parseIds = useCallback((raw: string | null): number[] => {
    if (!raw) return [];
    return raw
      .split(',')
      .map((x) => Number(x.trim()))
      .filter((n) => Number.isInteger(n) && n > 0);
  }, []);
  const parseTriBool = useCallback((raw: string | null): TriBool => {
    return raw === 'true' || raw === 'false' ? raw : '';
  }, []);
  const [showNew, setShowNew] = useState(false);
  const [goods, setGoods] = useState<GoodsProps[]>([]);
  const [productCategories, setProductCategories] = useState<
    { id: number; name: string }[]
  >([]);
  const [brands, setBrands] = useState<{ name: string }[]>([]);
  const [availableTags, setAvailableTags] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(
    parseIds(searchParams.get('tag_ids'))
  );
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get('page'));
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [total, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [limit] = useState(50);
  const [filter, setFilter] = useState(searchParams.get('article') || '');
  const [showDeleted, setShowDeleted] = useState(
    searchParams.get('deleted') === 'true'
  );
  const [isCombo, setIsCombo] = useState<TriBool>(
    parseTriBool(searchParams.get('is_combo'))
  );
  const [fbs, setFbs] = useState<TriBool>(
    parseTriBool(searchParams.get('show_for_distribution'))
  );
  const [mpAuths, setMpAuths] = useState<MpAuthOption[]>([]);
  const [linkedAuthIds, setLinkedAuthIds] = useState<number[]>(
    parseIds(searchParams.get('mp_linked_auth_ids'))
  );
  const [unlinkedAuthIds, setUnlinkedAuthIds] = useState<number[]>(
    parseIds(searchParams.get('mp_unlinked_auth_ids'))
  );
  const [brand, setBrand] = useState<string>(searchParams.get('brand') || '');
  const [category, setCategory] = useState<string>(
    searchParams.get('category') || ''
  );
  const [pending, setPending] = useState(false);

  const fetchGoods = useCallback(async (queryParams?: GoodsQueryParams) => {
    setPending(true);
    try {
      const result = await GoodsApi.getAll(queryParams);

      if (queryParams && Object.keys(queryParams).length > 0) {
        const paginatedResult = result as GoodsPaginatedResponse;
        setGoods(paginatedResult.data);
        setTotal(paginatedResult.total);
        setTotalPage(paginatedResult.total_page);
        setPage(paginatedResult.page);
      } else {
        const goodsArray = result as GoodsProps[];
        setGoods(goodsArray);
        setTotal(goodsArray.length);
        setTotalPage(1);
        setPage(1);
      }
    } catch (error) {
      console.error('Error fetching goods:', error);
    } finally {
      setPending(false);
    }
  }, []);

  const refreshTags = useCallback(async () => {
    const tags = await GoodsTagsApi.getAll();
    setAvailableTags(tags.map((t) => ({ id: t.id, name: t.name })));
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      const [cats, brandList, auths] = await Promise.all([
        GoodsApi.getCategories(),
        GoodsApi.getBrands(),
        MpProductsApi.listAuths().catch(() => [] as MpAuthOption[]),
      ]);
      setProductCategories(cats);
      setBrands(brandList);
      setMpAuths(auths);
      await refreshTags();
    };
    loadInitialData();
  }, [refreshTags]);

  const buildQuery = useCallback(
    (pageOverride?: number): GoodsQueryParams => {
      const queryParams: GoodsQueryParams = {
        page: pageOverride ?? page,
        limit,
      };
      if (showDeleted) queryParams.deleted = true;
      if (filter) {
        queryParams.name = filter;
        queryParams.article = filter;
        queryParams.alias = filter;
      }
      if (selectedTagIds.length) queryParams.tag_ids = selectedTagIds;
      if (isCombo === 'true') queryParams.is_combo = true;
      if (isCombo === 'false') queryParams.is_combo = false;
      if (fbs === 'true') queryParams.show_for_distribution = true;
      if (fbs === 'false') queryParams.show_for_distribution = false;
      if (linkedAuthIds.length)
        queryParams.mp_linked_auth_ids = linkedAuthIds;
      if (unlinkedAuthIds.length)
        queryParams.mp_unlinked_auth_ids = unlinkedAuthIds;
      if (brand) queryParams.brand = brand;
      if (category) queryParams.category = category;
      return queryParams;
    },
    [
      page,
      limit,
      showDeleted,
      filter,
      selectedTagIds,
      isCombo,
      fbs,
      linkedAuthIds,
      unlinkedAuthIds,
      brand,
      category,
    ]
  );

  useEffect(() => {
    fetchGoods(buildQuery());
  }, [buildQuery, fetchGoods]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (filter) next.set('article', filter);
    if (showDeleted) next.set('deleted', 'true');
    if (isCombo) next.set('is_combo', isCombo);
    if (fbs) next.set('show_for_distribution', fbs);
    if (brand) next.set('brand', brand);
    if (category) next.set('category', category);
    if (selectedTagIds.length) next.set('tag_ids', selectedTagIds.join(','));
    if (linkedAuthIds.length)
      next.set('mp_linked_auth_ids', linkedAuthIds.join(','));
    if (unlinkedAuthIds.length)
      next.set('mp_unlinked_auth_ids', unlinkedAuthIds.join(','));
    if (page > 1) next.set('page', String(page));
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    filter,
    showDeleted,
    isCombo,
    fbs,
    brand,
    category,
    selectedTagIds,
    linkedAuthIds,
    unlinkedAuthIds,
    page,
    searchParams,
    setSearchParams,
  ]);

  const createGoods = async (props: GoodsProps) => {
    await GoodsApi.create(props);
    await refreshTags();
    await fetchGoods(buildQuery());
    setShowNew(false);
  };

  const updateGoods = async (props: GoodsProps) => {
    const result = await GoodsApi.edit(props.article, props);
    await refreshTags();
    setGoods((prev) =>
      prev.map((el) =>
        el.article === props.article
          ? {
              ...result,
              mp_links: result.mp_links ?? el.mp_links,
              mp_linked_count: result.mp_linked_count ?? el.mp_linked_count,
              mp_auth_total: result.mp_auth_total ?? el.mp_auth_total,
              mp_barcodes: result.mp_barcodes ?? el.mp_barcodes,
            }
          : el
      )
    );
    return result;
  };

  const deleteGoods = async (article: GoodsProps['article']) => {
    await GoodsApi.remove(article);
    await fetchGoods(buildQuery());
  };

  const restoreGoods = async (article: GoodsProps['article']) => {
    await GoodsApi.restore(article);
    await fetchGoods(buildQuery());
  };

  const resetPage = () => setPage(1);

  const handleFilterChange = (value: string) => {
    setFilter(value);
    resetPage();
  };

  const handleTagFilterChange = (ids: number[]) => {
    setSelectedTagIds(ids);
    resetPage();
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const hasActiveFilters =
    !!filter ||
    !!selectedTagIds.length ||
    !!isCombo ||
    !!fbs ||
    !!linkedAuthIds.length ||
    !!unlinkedAuthIds.length ||
    !!brand ||
    !!category ||
    showDeleted;

  const hasFbsBulkScope =
    !showDeleted &&
    (!!filter ||
      !!selectedTagIds.length ||
      !!isCombo ||
      !!linkedAuthIds.length ||
      !!unlinkedAuthIds.length ||
      !!brand ||
      !!category);

  const enableFbsByFilter = async () => {
    const query = buildQuery();
    delete query.page;
    delete query.limit;
    delete query.deleted;
    const result = await GoodsApi.enableFbsByFilter(query);
    await fetchGoods(buildQuery());
    return result;
  };

  return {
    goods,
    createGoods,
    showNew,
    setShowNew,
    deleteGoods,
    restoreGoods,
    updateGoods,
    brands,
    productCategories,
    availableTags,
    mpAuths,
    refreshTags,
    selectedTagIds,
    setSelectedTagIds: handleTagFilterChange,
    page,
    totalPage,
    total,
    filter,
    setFilter: handleFilterChange,
    handlePageChange,
    showDeleted,
    setShowDeleted: (v: boolean) => {
      setShowDeleted(v);
      resetPage();
    },
    isCombo,
    setIsCombo: (v: TriBool) => {
      setIsCombo(v);
      resetPage();
    },
    fbs,
    setFbs: (v: TriBool) => {
      setFbs(v);
      resetPage();
    },
    linkedAuthIds,
    setLinkedAuthIds: (ids: number[]) => {
      setLinkedAuthIds(ids);
      resetPage();
    },
    unlinkedAuthIds,
    setUnlinkedAuthIds: (ids: number[]) => {
      setUnlinkedAuthIds(ids);
      resetPage();
    },
    brand,
    setBrand: (v: string) => {
      setBrand(v);
      resetPage();
    },
    category,
    setCategory: (v: string) => {
      setCategory(v);
      resetPage();
    },
    hasActiveFilters,
    hasFbsBulkScope,
    enableFbsByFilter,
    pending,
  };
};
